// LVA bulk-register import configuration (Phase D8 — create-only).
// Mirrors components/journal/jvImport.ts: page-agnostic field/template/preview
// specs live here, and the parse/runImport logic is exposed as factory functions
// that close over the company masters + the create mutation supplied by the page,
// so they stay unit-testable without mounting the route.

import { CreateLvaItemSchema, fmt } from '@dcc-erp/shared';
import type { CreateLvaItem } from '@dcc-erp/shared';

import { headerIndexer } from '../../lib/xlsx';

import type {
  ImportFieldSpec,
  ImportResult,
  ParsedUnit,
  PreviewColumn,
} from '../SpreadsheetImportModal';

// ---- Template / header keys ------------------------------------------------
// ONE spreadsheet row = ONE LvaItem. The first six columns are required; the
// rest are optional. branchCode/departmentCode are resolved to ids against the
// company masters at parse time; categoryId (if given) is a raw uuid passthrough.
export const LVA_IMPORT_COLS = [
  'name',
  'categoryName',
  'acquiredDate',
  'cost',
  'location',
  'assignedTo',
  'branchCode',
  'departmentCode',
  'categoryId',
  'condition',
  'quantity',
  'unit',
  'serialNumber',
  'warrantyExpiry',
  'sourceDocRef',
] as const;

const REQUIRED_COLS = [
  'name',
  'categoryName',
  'acquiredDate',
  'cost',
  'location',
  'assignedTo',
] as const;

export const LVA_IMPORT_FIELDS: ImportFieldSpec[] = [
  { col: 'name', label: 'ชื่อสินทรัพย์', required: true, hint: 'เช่น เก้าอี้สำนักงาน' },
  { col: 'categoryName', label: 'หมวด', required: true, hint: 'เช่น เฟอร์นิเจอร์สำนักงาน' },
  {
    col: 'acquiredDate',
    label: 'วันที่ได้มา',
    required: true,
    hint: 'รูปแบบ YYYY-MM-DD เช่น 2026-01-05',
  },
  { col: 'cost', label: 'มูลค่า', required: true, hint: 'จำนวนเงิน > 0' },
  { col: 'location', label: 'สถานที่ใช้งาน', required: true, hint: 'เช่น สำนักงานใหญ่ ชั้น 14' },
  { col: 'assignedTo', label: 'ผู้รับผิดชอบ', required: true, hint: 'ชื่อผู้ครอบครอง' },
  {
    col: 'branchCode',
    label: 'รหัสสาขา',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุ ต้องมีอยู่จริง',
  },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'เว้นว่างได้ — ถ้าระบุ ต้องมีอยู่จริง',
  },
  {
    col: 'categoryId',
    label: 'รหัสหมวด (uuid)',
    required: false,
    hint: 'เว้นว่างได้ — FK หมวดสินทรัพย์',
  },
  {
    col: 'condition',
    label: 'สภาพ',
    required: false,
    hint: 'NEW / GOOD / FAIR / POOR / DAMAGED (ค่าเริ่มต้น GOOD)',
  },
  { col: 'quantity', label: 'จำนวน', required: false, hint: 'จำนวนเต็ม > 0 (ค่าเริ่มต้น 1)' },
  { col: 'unit', label: 'หน่วย', required: false, hint: 'เช่น ตัว / ชุด' },
  { col: 'serialNumber', label: 'Serial No.', required: false, hint: 'เว้นว่างได้' },
  {
    col: 'warrantyExpiry',
    label: 'วันหมดประกัน',
    required: false,
    hint: 'รูปแบบ YYYY-MM-DD — เว้นว่างได้',
  },
  {
    col: 'sourceDocRef',
    label: 'เอกสารอ้างอิง',
    required: false,
    hint: 'เช่น PO/ใบเสร็จ — เว้นว่างได้',
  },
];

export const LVA_TEMPLATE_EXAMPLES: (string | number)[][] = [
  [
    'เก้าอี้ Ergonomic',
    'เฟอร์นิเจอร์สำนักงาน',
    '2026-01-05',
    4000,
    'สำนักงานใหญ่ ชั้น 14',
    'สมชาย',
    '',
    '',
    '',
    'NEW',
    1,
    'ตัว',
    'SN-001',
    '',
    'PO-2026-001',
  ],
  [
    'กล้องวงจรปิด',
    'กล้องวงจรปิด / Security',
    '2026-02-01',
    3000,
    'หน้าโกดัง',
    'สมหญิง',
    '',
    '',
    '',
    'GOOD',
    1,
    '',
    '',
    '',
    '',
  ],
];

export const LVA_PREVIEW_COLS: PreviewColumn<CreateLvaItem>[] = [
  { header: 'ชื่อ', render: (d) => d.name },
  { header: 'หมวด', width: 160, render: (d) => d.categoryName },
  { header: 'วันที่ได้มา', width: 110, render: (d) => d.acquiredDate },
  { header: 'มูลค่า', width: 110, render: (d) => fmt(Number(d.cost)) },
];

// ---- Parse factory ---------------------------------------------------------
// Closes over the company masters so the page can supply live branch/department
// code → id maps (keys lower-cased, matching the journal importer convention).
export interface LvaImportMaps {
  branchByCode: Map<string, string>;
  departmentByCode: Map<string, string>;
}

export function makeLvaParse(maps: LvaImportMaps) {
  return function parse(grid: string[][]): {
    units: ParsedUnit<CreateLvaItem>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of REQUIRED_COLS) {
      if (idx(req) === -1) return { units: [], headerError: `ไม่พบคอลัมน์ที่จำเป็น: ${req}` };
    }

    const units: ParsedUnit<CreateLvaItem>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const branchCode = cell(cols, 'branchCode');
      const branchId = branchCode ? maps.branchByCode.get(branchCode.toLowerCase()) : null;
      if (branchCode && !branchId) {
        units.push({ rowNo, data: null, error: `ไม่พบสาขา: ${branchCode}`, action: 'create' });
        continue;
      }
      const departmentCode = cell(cols, 'departmentCode');
      const departmentId = departmentCode
        ? maps.departmentByCode.get(departmentCode.toLowerCase())
        : null;
      if (departmentCode && !departmentId) {
        units.push({
          rowNo,
          data: null,
          error: `ไม่พบหน่วยงาน: ${departmentCode}`,
          action: 'create',
        });
        continue;
      }

      // Build the candidate from non-empty cells only; the schema applies the
      // defaults (condition=GOOD, quantity=1) and coerces cost/quantity.
      const candidate: Record<string, unknown> = {
        name: cell(cols, 'name'),
        categoryName: cell(cols, 'categoryName'),
        acquiredDate: cell(cols, 'acquiredDate'),
        cost: cell(cols, 'cost'),
        location: cell(cols, 'location'),
        assignedTo: cell(cols, 'assignedTo'),
        branchId: branchId ?? null,
        departmentId: departmentId ?? null,
      };
      const categoryId = cell(cols, 'categoryId');
      if (categoryId) candidate.categoryId = categoryId;
      const condition = cell(cols, 'condition');
      if (condition) candidate.condition = condition.toUpperCase();
      const quantity = cell(cols, 'quantity');
      if (quantity) candidate.quantity = quantity;
      const unit = cell(cols, 'unit');
      if (unit) candidate.unit = unit;
      const serialNumber = cell(cols, 'serialNumber');
      if (serialNumber) candidate.serialNumber = serialNumber;
      const warrantyExpiry = cell(cols, 'warrantyExpiry');
      if (warrantyExpiry) candidate.warrantyExpiry = warrantyExpiry;
      const sourceDocRef = cell(cols, 'sourceDocRef');
      if (sourceDocRef) candidate.sourceDocRef = sourceDocRef;

      const parsed = CreateLvaItemSchema.safeParse(candidate);
      units.push(
        parsed.success
          ? { rowNo, data: parsed.data, error: null, action: 'create' }
          : {
              rowNo,
              data: null,
              error: parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง',
              action: 'create',
            },
      );
    }
    return { units, headerError: null };
  };
}

// ---- runImport factory -----------------------------------------------------
// Create-only: one POST per valid row with a fresh idempotency key. No update
// path. Closes over the create mutation's mutateAsync (signature mirrors
// useCreateLvaItem). Per-row failures are tallied, never abort the batch.
export type CreateLvaItemMutate = (args: {
  data: CreateLvaItem;
  idempotencyKey?: string;
}) => Promise<unknown>;

export function makeLvaRunImport(createItem: CreateLvaItemMutate) {
  return async function runImport(
    valid: ParsedUnit<CreateLvaItem>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createItem({ data: u.data, idempotencyKey: crypto.randomUUID() });
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: `แถว ${u.rowNo}`,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  };
}
